import React from 'react';
import {
    Portal,
    IconButton,
    TextField,
    Tooltip,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import AutocompleteSelectFilter from '../../Widgets/AutocompleteSelectFilter/AutocompleteSelectFilter.jsx';
import CustomDatePicker from '../../Widgets/CustomDates/CustomDatePicker/CustomDatePicker.js';
import { useTranslation } from 'react-i18next';
import Constants from '../../../assests/Constants/constants.js';
import styles from './BulkEditDialog.module.scss';

const BulkEditDialog = ({
    open,
    onClose,
    direction,
    palCodesOptions,
    sortCodesOptions,
    selectedPalCodeForSelectAll,
    setSelectedPalCodeForSelectAll,
    selectedSortCodeForSelectAll,
    setSelectedSortCodeForSelectAll,
    selectedAutoProcessedForSelectAll,
    setSelectedAutoProcessedForSelectAll,
    selectedSourceDeductionForSelectAll,
    setSelectedSourceDeductionForSelectAll,
    selectedDeductionTypeForSelectAll,
    setSelectedDeductionTypeForSelectAll,
    selectedTaxAssessorForSelectAll,
    setSelectedTaxAssessorForSelectAll,
    selectedDeductionRateForSelectAll,
    setSelectedDeductionRateForSelectAll,
    selectedDeductionValidityForSelectAll,
    setSelectedDeductionValidityForSelectAll,
    // Optional payment-terms bulk edit (only rendered when wired in by the parent).
    selectedPaymentTermsTypeForSelectAll,
    setSelectedPaymentTermsTypeForSelectAll,
    selectedPaymentTermsDaysForSelectAll,
    setSelectedPaymentTermsDaysForSelectAll,
    // Optional cash-flow bulk edit (only rendered when wired in by the parent).
    selectedIncludeInCashFlowForSelectAll,
    setSelectedIncludeInCashFlowForSelectAll,
    selectedCashFlowCodeForSelectAll,
    setSelectedCashFlowCodeForSelectAll,
    selectedCashFlowAverageAmountForSelectAll,
    setSelectedCashFlowAverageAmountForSelectAll,
    selectedCashFlowGroupForSelectAll,
    setSelectedCashFlowGroupForSelectAll,
    onSave,
    userTypeContext
}) => {
    const { t } = useTranslation();

    const sourceDeductionOptions = [
        { value: 0, label: t('NotIncluded') },
        { value: 1, label: t('Included') },
    ];

    const supportsPaymentTerms = typeof setSelectedPaymentTermsTypeForSelectAll === 'function';
    const paymentTermsOptions = (Constants.PaymentTermsTypeOptions || []).map((o) => ({
        value: o.value,
        label: t(`PaymentTerms_${o.label}`, o.label),
    }));
    const isCustomPaymentTerms = Number(selectedPaymentTermsTypeForSelectAll) === Constants.PaymentTermsType?.Custom;

    const supportsCashFlow = typeof setSelectedCashFlowCodeForSelectAll === 'function';
    const supportsCashFlowAverageAmount = typeof setSelectedCashFlowAverageAmountForSelectAll === 'function';
    const supportsCashFlowGroup = typeof setSelectedCashFlowGroupForSelectAll === 'function';

    const handleSave = () => {
        onSave();
    };

    const handleCancel = () => {
        // Reset values
        setSelectedPalCodeForSelectAll('');
        setSelectedSortCodeForSelectAll('');
        setSelectedAutoProcessedForSelectAll(false);
        setSelectedSourceDeductionForSelectAll?.('');
        setSelectedDeductionTypeForSelectAll?.('');
        setSelectedTaxAssessorForSelectAll?.('');
        setSelectedDeductionRateForSelectAll?.('');
        setSelectedDeductionValidityForSelectAll?.('');
        setSelectedPaymentTermsTypeForSelectAll?.('');
        setSelectedPaymentTermsDaysForSelectAll?.('');
        setSelectedIncludeInCashFlowForSelectAll?.(false);
        setSelectedCashFlowCodeForSelectAll?.('');
        setSelectedCashFlowAverageAmountForSelectAll?.('');
        setSelectedCashFlowGroupForSelectAll?.('');
        onClose();
    };

    if (!open) return null;

    const textAlign = direction === 'rtl' ? 'right' : 'left';
    const datePickerFieldClassName = `${styles.styleInput} ${styles.dateField}`;
    const datePickerInputSx = {
        border: '0 !important',
        borderRadius: '0 !important',
        background: '#f5f5f6 !important',
        height: '40px',
        minHeight: '40px',
        boxSizing: 'border-box',
        boxShadow: 'none !important',
        outline: 'none !important',
        padding: '0 8px',
        fontSize: '15px',
        fontWeight: 500,
        '&:hover, &:focus, &.Mui-focused, &:focus-within': {
            border: '0 !important',
            boxShadow: 'none !important',
            outline: 'none !important',
        },
        '&::before, &::after': {
            display: 'none !important',
            border: '0 !important',
        },
        '& fieldset': {
            border: '0 !important',
        },
        '& input': {
            fontSize: '15px !important',
            fontWeight: 500,
            textAlign,
        },
    };

    return (
        <Portal>
            <div
                className={`${styles.overlay} ${direction === 'rtl' ? styles.rtl : styles.ltr}`}
                onClick={handleCancel}
            >
                <div dir={direction} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modal}>
                        <div className={styles.body}>
                            <div className={styles.header}>
                                <Tooltip title={t('Close') || 'Close'}>
                                    <IconButton onClick={handleCancel} className={styles.closeIcon}>
                                        <CancelIcon className={styles.closeIcon} />
                                    </IconButton>
                                </Tooltip>
                                <div>{t('ChangeFieldsForAllSelected')}</div>
                                <div />
                            </div>

                            <div className={styles.scroll}>
                                <div className={styles.fields}>
                                    <div className={styles.formField}>
                                        <span className={styles.fieldLabel} dir={direction}>
                                            {t('SelectBalanceClassification')}
                                        </span>
                                        <AutocompleteSelectFilter
                                            options={palCodesOptions || []}
                                            selectedValues={
                                                (palCodesOptions || []).find(
                                                    (option) => option.value === selectedPalCodeForSelectAll
                                                ) || null
                                            }
                                            onChange={(event, newValue) =>
                                                setSelectedPalCodeForSelectAll(newValue ? newValue.value : null)
                                            }
                                            label={t('SelectBalanceClassification')}
                                            placeholder={t('SelectBalanceClassification')}
                                            isMultiple={false}
                                            direction={direction}
                                            portalMenu
                                            searchable
                                        />
                                    </div>

                                    {userTypeContext?.toString() !== Constants.UserType.Client && (
                                        <>
                                            <div className={styles.formField}>
                                                <span className={styles.fieldLabel} dir={direction}>
                                                    {t('SelectSortCode')}
                                                </span>
                                                <AutocompleteSelectFilter
                                                    options={sortCodesOptions || []}
                                                    selectedValues={
                                                        (sortCodesOptions || []).find(
                                                            (option) => option.value === selectedSortCodeForSelectAll
                                                        ) || null
                                                    }
                                                    onChange={(event, newValue) =>
                                                        setSelectedSortCodeForSelectAll(newValue ? newValue.value : null)
                                                    }
                                                    label={t('SelectSortCode')}
                                                    placeholder={t('SelectSortCode')}
                                                    isMultiple={false}
                                                    direction={direction}
                                                    portalMenu
                                                    searchable
                                                />
                                            </div>
                                            <label className={styles.checkboxRow} htmlFor="autoProcessedCheckbox">
                                                <input
                                                    type="checkbox"
                                                    id="autoProcessedCheckbox"
                                                    checked={selectedAutoProcessedForSelectAll}
                                                    onChange={(e) => setSelectedAutoProcessedForSelectAll(e.target.checked)}
                                                />
                                                <span>{t('AutoProcessed')}</span>
                                            </label>
                                        </>
                                    )}

                                    <div className={styles.formField}>
                                        <span className={styles.fieldLabel} dir={direction}>
                                            {t('SourceDeductionAssociation')}
                                        </span>
                                        <AutocompleteSelectFilter
                                            options={sourceDeductionOptions}
                                            selectedValues={
                                                sourceDeductionOptions.find(
                                                    (option) => option.value === selectedSourceDeductionForSelectAll
                                                ) || null
                                            }
                                            onChange={(event, newValue) =>
                                                setSelectedSourceDeductionForSelectAll(
                                                    newValue !== null && newValue !== undefined ? newValue.value : ''
                                                )
                                            }
                                            label={t('SourceDeductionAssociation')}
                                            placeholder={t('SourceDeductionAssociation')}
                                            isMultiple={false}
                                            direction={direction}
                                            portalMenu
                                            searchable
                                        />
                                    </div>

                                    <div className={styles.formField}>
                                        <span className={styles.fieldLabel} dir={direction}>
                                            {t('DeductionType')}
                                        </span>
                                        <AutocompleteSelectFilter
                                            options={Constants.DeductionTypeOptions || []}
                                            selectedValues={
                                                (Constants.DeductionTypeOptions || []).find(
                                                    (option) => option.value === selectedDeductionTypeForSelectAll
                                                ) || null
                                            }
                                            onChange={(event, newValue) =>
                                                setSelectedDeductionTypeForSelectAll(
                                                    newValue !== null && newValue !== undefined ? newValue.value : ''
                                                )
                                            }
                                            label={t('DeductionType')}
                                            placeholder={t('DeductionType')}
                                            isMultiple={false}
                                            direction={direction}
                                            portalMenu
                                            searchable
                                        />
                                    </div>

                                    <div className={styles.formField}>
                                        <span className={styles.fieldLabel} dir={direction}>
                                            {t('TaxAssessor')}
                                        </span>
                                        <AutocompleteSelectFilter
                                            options={Constants.TaxAssessorOptionsString || []}
                                            selectedValues={
                                                (Constants.TaxAssessorOptionsString || []).find(
                                                    (option) => option.value === selectedTaxAssessorForSelectAll
                                                ) || null
                                            }
                                            onChange={(event, newValue) =>
                                                setSelectedTaxAssessorForSelectAll(
                                                    newValue !== null && newValue !== undefined ? newValue.value : ''
                                                )
                                            }
                                            label={t('TaxAssessor')}
                                            placeholder={t('TaxAssessor')}
                                            isMultiple={false}
                                            direction={direction}
                                            portalMenu
                                            searchable
                                        />
                                    </div>

                                    <div className={styles.formField}>
                                        <span className={styles.fieldLabel} dir={direction}>
                                            {t('DeductionRate')}
                                        </span>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            variant="standard"
                                            type="number"
                                            className={styles.styleInput}
                                            inputProps={{ step: '0.01', min: 0, style: { textAlign } }}
                                            value={selectedDeductionRateForSelectAll}
                                            onChange={(e) => setSelectedDeductionRateForSelectAll(e.target.value)}
                                            placeholder={t('DeductionRate')}
                                            InputProps={{ disableUnderline: true }}
                                        />
                                    </div>

                                    <div className={styles.formField}>
                                        <span className={styles.fieldLabel} dir={direction}>
                                            {t('DeductionValidity')}
                                        </span>
                                        <CustomDatePicker
                                            date={selectedDeductionValidityForSelectAll ? selectedDeductionValidityForSelectAll : null}
                                            onDateChange={(d) => setSelectedDeductionValidityForSelectAll(d ? d.toISOString() : '')}
                                            label=""
                                            height="40px"
                                            textFieldClassName={datePickerFieldClassName}
                                            inputSx={datePickerInputSx}
                                        />
                                    </div>

                                    {supportsPaymentTerms && (
                                        <div className={`${styles.section} ${styles.fieldsFull}`}>
                                            <p className={styles.sectionTitle}>{t('PaymentTerms')}</p>
                                            <div className={styles.formField}>
                                                <span className={styles.fieldLabel} dir={direction}>
                                                    {t('PaymentTerms_Placeholder', 'e.g. Net 30 days')}
                                                </span>
                                                <AutocompleteSelectFilter
                                                    options={paymentTermsOptions}
                                                    selectedValues={
                                                        paymentTermsOptions.find(
                                                            (option) =>
                                                                Number(option.value) === Number(selectedPaymentTermsTypeForSelectAll)
                                                        ) || null
                                                    }
                                                    onChange={(event, newValue) =>
                                                        setSelectedPaymentTermsTypeForSelectAll(
                                                            newValue !== null && newValue !== undefined ? newValue.value : ''
                                                        )
                                                    }
                                                    label={t('PaymentTerms')}
                                                    placeholder={t('PaymentTerms_Placeholder', 'e.g. Net 30 days')}
                                                    isMultiple={false}
                                                    direction={direction}
                                                    portalMenu
                                                    searchable
                                                />
                                            </div>
                                            {isCustomPaymentTerms && (
                                                <div className={styles.formField}>
                                                    <span className={styles.fieldLabel} dir={direction}>
                                                        {t('PaymentTermsCustomDays', 'Custom days')}
                                                    </span>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        variant="standard"
                                                        type="number"
                                                        className={styles.styleInput}
                                                        inputProps={{ min: 0, style: { textAlign } }}
                                                        value={selectedPaymentTermsDaysForSelectAll ?? ''}
                                                        onChange={(e) => setSelectedPaymentTermsDaysForSelectAll(e.target.value)}
                                                        placeholder={t('PaymentTermsCustomDaysPlaceholder', 'Days until due')}
                                                        InputProps={{ disableUnderline: true }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {supportsCashFlow && (
                                        <div className={`${styles.section} ${styles.fieldsFull}`}>
                                            <p className={styles.sectionTitle}>
                                                {t('CashFlowForecast', 'דוח תזרים מזומנים')}
                                            </p>
                                            <label className={styles.checkboxRow} htmlFor="includeInCashFlowCheckbox">
                                                <input
                                                    type="checkbox"
                                                    id="includeInCashFlowCheckbox"
                                                    checked={Boolean(selectedIncludeInCashFlowForSelectAll)}
                                                    onChange={(e) => setSelectedIncludeInCashFlowForSelectAll?.(e.target.checked)}
                                                />
                                                <span>{t('IncludeInCashFlow', 'כלול בתזרים')}</span>
                                            </label>
                                            <div className={styles.formField}>
                                                <span className={styles.fieldLabel} dir={direction}>
                                                    {t('CashFlowCode', 'קוד תזרים')}
                                                </span>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    variant="standard"
                                                    type="number"
                                                    className={styles.styleInput}
                                                    inputProps={{ min: 1, style: { textAlign } }}
                                                    value={selectedCashFlowCodeForSelectAll ?? ''}
                                                    onChange={(e) => setSelectedCashFlowCodeForSelectAll(e.target.value)}
                                                    disabled={!selectedIncludeInCashFlowForSelectAll}
                                                    placeholder={t('CashFlowCodePlaceholder', 'לדוגמה: 1')}
                                                    InputProps={{ disableUnderline: true }}
                                                />
                                            </div>
                                            {supportsCashFlowAverageAmount && (
                                                <div className={styles.formField}>
                                                    <span className={styles.fieldLabel} dir={direction}>
                                                        {t('CashFlowAverageAmount', 'ממוצע הוצאה בתקופה')}
                                                    </span>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        variant="standard"
                                                        type="number"
                                                        className={styles.styleInput}
                                                        inputProps={{ style: { textAlign } }}
                                                        value={selectedCashFlowAverageAmountForSelectAll ?? ''}
                                                        onChange={(e) => setSelectedCashFlowAverageAmountForSelectAll?.(e.target.value)}
                                                        disabled={!selectedIncludeInCashFlowForSelectAll}
                                                        placeholder={t('CashFlowAverageAmountPlaceholder', 'לדוגמה: 2000')}
                                                        InputProps={{ disableUnderline: true }}
                                                    />
                                                </div>
                                            )}
                                            {supportsCashFlowGroup && (
                                                <div className={styles.formField}>
                                                    <span className={styles.fieldLabel} dir={direction}>
                                                        {t('CashFlowGroup', 'קבוצת תזרים')}
                                                    </span>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        variant="standard"
                                                        className={styles.styleInput}
                                                        inputProps={{ style: { textAlign } }}
                                                        value={selectedCashFlowGroupForSelectAll ?? ''}
                                                        onChange={(e) => setSelectedCashFlowGroupForSelectAll?.(e.target.value)}
                                                        disabled={!selectedIncludeInCashFlowForSelectAll}
                                                        InputProps={{ disableUnderline: true }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className={styles.footer}>
                                <button type="button" className={styles.secondaryBtn} onClick={handleCancel}>
                                    {t('Cancel')}
                                </button>
                                <button type="button" className={styles.primaryBtn} onClick={handleSave}>
                                    {t('Save')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default BulkEditDialog;
